import React from "react";

/* Deterministic HSL color from a string (user_id or email). */
function strHash(str) {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function avatarBgColor(seed) {
  const palette = [
    "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
    "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16",
    "#06b6d4", "#a855f7", "#dc2626", "#059669", "#7c3aed",
  ];
  return palette[strHash(seed || "default") % palette.length];
}

function getInitials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const SIZES = {
  xs: { box: "w-4 h-4", text: "text-[7px]", ring: "ring-[1px]" },
  sm: { box: "w-6 h-6", text: "text-[9px]", ring: "ring-1" },
  md: { box: "w-8 h-8", text: "text-[11px]", ring: "ring-2" },
  lg: { box: "w-10 h-10", text: "text-xs", ring: "ring-2" },
  xl: { box: "w-14 h-14", text: "text-base", ring: "ring-2" },
};

/**
 * Round avatar for a user. Falls back to initials with deterministic color
 * when no picture is set. `user` accepts either a user object or a plain name string.
 */
export default function UserAvatar({
  user,
  name,
  size = "sm",
  ringColor,
  className = "",
  title,
  showTitle = true,
}) {
  const u = typeof user === "string" ? { name: user } : (user || {});
  const display = name || u.name || u.full_name || u.email || "?";
  const seed = u.user_id || u.email || display;
  const initials = getInitials(display);
  const bg = avatarBgColor(seed);
  const cfg = SIZES[size] || SIZES.sm;
  const tooltip = title ?? (showTitle ? display : undefined);
  const ringStyle = ringColor ? { boxShadow: `0 0 0 2px ${ringColor}` } : undefined;

  if (u.picture || u.avatar_url) {
    const raw = u.picture || u.avatar_url;
    // If it's a relative API path, prefix with backend URL so static files load correctly.
    let src = raw;
    if (typeof raw === "string" && raw.startsWith("/api/")) {
      const base = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
      src = `${base}${raw}`;
    }
    return (
      <img
        src={src}
        alt={display}
        title={tooltip}
        className={`${cfg.box} rounded-full object-cover ring-1 ring-white shadow-sm flex-shrink-0 ${className}`}
        style={ringStyle}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        data-testid={`user-avatar-${seed}`}
      />
    );
  }

  return (
    <span
      className={`${cfg.box} ${cfg.text} rounded-full inline-flex items-center justify-center font-bold text-white ring-1 ring-white shadow-sm flex-shrink-0 select-none ${className}`}
      style={{ backgroundColor: bg, ...ringStyle }}
      title={tooltip}
      data-testid={`user-avatar-${seed}`}
    >
      {initials}
    </span>
  );
}

/**
 * Stacked row of avatars (max N + "+M" indicator). Used to show multiple participants
 * in a compact way (e.g. project participants on a Gantt row).
 */
export function UserAvatarStack({ users = [], max = 3, size = "sm", className = "" }) {
  const visible = users.slice(0, max);
  const overflow = users.length - max;
  const cfg = SIZES[size] || SIZES.sm;
  return (
    <div className={`inline-flex items-center -space-x-1.5 ${className}`} data-testid="user-avatar-stack">
      {visible.map((u, i) => (
        <UserAvatar key={u.user_id || u.email || i} user={u} size={size} />
      ))}
      {overflow > 0 && (
        <span
          className={`${cfg.box} ${cfg.text} rounded-full inline-flex items-center justify-center font-bold text-slate-600 bg-slate-200 ring-1 ring-white`}
          title={`a další ${overflow}`}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
